import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import type { Coffee } from "../types";
import { KIND_OPTIONS, kindLabel } from "../types";

type Edits = Record<number, number>;

export function Stocktake() {
  const [coffees, setCoffees] = useState<Coffee[] | null>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  type SortKey = "name" | "portions" | "country" | "roaster";
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [kind, setKind] = useState("coffee");

  useEffect(() => {
    api.list().then(setCoffees).catch((e) => setError((e as Error).message));
  }, []);

  const sorted = useMemo(() => {
    if (!coffees) return [];
    const arr = coffees.filter((c) => kind === "" || c.kind === kind);
    const cmp = (a: Coffee, b: Coffee): number => {
      switch (sortKey) {
        case "portions": return a.portions - b.portions;
        case "country":  return a.country.localeCompare(b.country);
        case "roaster":  return a.roaster.localeCompare(b.roaster);
        default:         return a.variety.localeCompare(b.variety);
      }
    };
    arr.sort((a, b) => sortDir === "asc" ? cmp(a, b) : cmp(b, a));
    return arr;
  }, [coffees, sortKey, sortDir, kind]);

  // Click a column header to sort by it; click again to flip direction.
  const onHeaderSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const sortIndicator = (key: SortKey) => (
    sortKey === key ? <span className="ml-1 text-paper-600">{sortDir === "asc" ? "▲" : "▼"}</span> : null
  );

  const setEdit = (id: number, value: number, current: number) => {
    setEdits((cur) => {
      const next = { ...cur };
      if (Number.isFinite(value) && value !== current) next[id] = value;
      else delete next[id];
      return next;
    });
  };

  const changedCount = Object.keys(edits).length;
  const totalDelta = useMemo(() => {
    if (!coffees) return 0;
    return Object.entries(edits).reduce((sum, [id, v]) => {
      const c = coffees.find((x) => x.id === Number(id));
      return c ? sum + (v - c.portions) : sum;
    }, 0);
  }, [edits, coffees]);

  const reset = () => { setEdits({}); setError(null); setSavedAt(null); };

  const save = async () => {
    if (changedCount === 0) return;
    setBusy(true); setError(null);
    try {
      const updated = await Promise.all(
        Object.entries(edits).map(([id, v]) =>
          api.update(Number(id), { portions: Math.max(0, v) }),
        ),
      );
      setCoffees((cur) => {
        if (!cur) return cur;
        const map = new Map(updated.map((c) => [c.id, c]));
        return cur.map((c) => map.get(c.id) || c);
      });
      setEdits({});
      setSavedAt(new Date());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!coffees) return <p className="text-paper-500">Loading…</p>;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="eyebrow">Inventory · Stocktake</div>
        <h1 className="font-display text-3xl">Adjust portions</h1>
        <p className="text-sm text-paper-600 max-w-xl">
          Recount the freezer and update the count for each coffee here. Edits stay
          local until you press Save. Edited rows are highlighted.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        <KindChip label="All" active={kind === ""} onClick={() => setKind("")} />
        {KIND_OPTIONS.map((k) => (
          <KindChip key={k} label={kindLabel(k)} active={kind === k} onClick={() => setKind(k)} />
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-4 pb-4 border-b border-paper-200">
        <div className="md:hidden flex items-end gap-2">
          <div className="flex flex-col">
            <label className="label">Sort by</label>
            <select className="bg-transparent border-0 border-b border-paper-300 text-sm py-1 pr-6 focus:outline-none focus:border-ink"
              value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
              <option value="name">Variety</option>
              <option value="country">Origin</option>
              <option value="roaster">Roaster</option>
              <option value="portions">Portions</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            className="px-2 py-1 border border-paper-300 text-sm hover:bg-paper-100"
            title={sortDir === "asc" ? "Ascending" : "Descending"}
          >
            {sortDir === "asc" ? "▲" : "▼"}
          </button>
        </div>
        <div className="text-xs text-paper-500 ml-auto flex items-center gap-4">
          {changedCount > 0 ? (
            <span>
              <span className="text-ink tabular-nums">{changedCount}</span> edited ·
              net change <span className={`tabular-nums ${totalDelta < 0 ? "text-red-700" : "text-paper-700"}`}>
                {totalDelta > 0 ? "+" : ""}{totalDelta}
              </span>
            </span>
          ) : null}
          {savedAt ? <span className="text-emerald-700">Saved {savedAt.toLocaleTimeString()}</span> : null}
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={reset} disabled={busy || changedCount === 0}>Discard</button>
          <button className="btn-primary" onClick={save} disabled={busy || changedCount === 0}>
            {busy ? "Saving…" : `Save${changedCount ? ` (${changedCount})` : ""}`}
          </button>
        </div>
      </div>

      {error ? <div className="text-red-700 text-sm">{error}</div> : null}

      {/* Desktop table */}
      <div className="hidden md:block">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-paper-500">
            <tr className="border-b border-paper-200">
              <th className="text-left py-2 font-medium">
                <button type="button" onClick={() => onHeaderSort("name")} className="hover:text-ink">
                  Coffee{sortIndicator("name")}
                </button>
              </th>
              <th className="text-left py-2 font-medium">
                <button type="button" onClick={() => onHeaderSort("country")} className="hover:text-ink">
                  Origin{sortIndicator("country")}
                </button>
              </th>
              <th className="text-left py-2 font-medium">
                <button type="button" onClick={() => onHeaderSort("roaster")} className="hover:text-ink">
                  Roaster{sortIndicator("roaster")}
                </button>
              </th>
              <th className="text-right py-2 font-medium w-24">
                <button type="button" onClick={() => onHeaderSort("portions")} className="hover:text-ink">
                  Current{sortIndicator("portions")}
                </button>
              </th>
              <th className="text-right py-2 font-medium w-32">New</th>
              <th className="text-right py-2 font-medium w-16">Δ</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => {
              const newVal = edits[c.id] ?? c.portions;
              const delta = newVal - c.portions;
              const changed = c.id in edits;
              return (
                <tr key={c.id} className={`border-b border-paper-200 ${changed ? "bg-paper-100" : ""}`}>
                  <td className="py-2 pr-3">
                    <Link to={`/coffee/${c.id}`} className="hover:underline">
                      <div className="font-medium text-ink">{c.variety}</div>
                      <div className="text-[11px] text-paper-500">{c.process}{c.roaster_sku ? ` · ${c.roaster_sku}` : ""}</div>
                    </Link>
                  </td>
                  <td className="py-2 pr-3 text-paper-600">{c.country}{c.region ? ` · ${c.region}` : ""}</td>
                  <td className="py-2 pr-3 text-paper-600">{c.roaster}</td>
                  <td className={`py-2 text-right tabular-nums ${c.portions <= 2 ? "text-red-700" : "text-paper-600"}`}>{c.portions}</td>
                  <td className="py-2 text-right">
                    <input
                      type="number" min={0}
                      value={newVal}
                      onChange={(e) => setEdit(c.id, Number(e.target.value), c.portions)}
                      className="w-20 bg-transparent border-0 border-b border-paper-300 text-right tabular-nums py-1 px-1 focus:outline-none focus:border-ink"
                    />
                  </td>
                  <td className={`py-2 text-right tabular-nums text-xs ${delta === 0 ? "text-paper-300" : delta > 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {delta === 0 ? "—" : (delta > 0 ? `+${delta}` : delta)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="md:hidden divide-y divide-paper-200 border-t border-paper-200">
        {sorted.map((c) => {
          const newVal = edits[c.id] ?? c.portions;
          const delta = newVal - c.portions;
          const changed = c.id in edits;
          return (
            <li key={c.id} className={`py-3 ${changed ? "bg-paper-100 -mx-4 px-4" : ""}`}>
              <div className="flex justify-between items-start gap-3">
                <Link to={`/coffee/${c.id}`} className="min-w-0">
                  <div className="font-medium text-ink truncate">{c.variety}</div>
                  <div className="text-[11px] text-paper-500 truncate">
                    {c.country}{c.roaster_sku ? ` · ${c.roaster_sku}` : ""} · {c.roaster}
                  </div>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-paper-400 uppercase tracking-widest">Now</span>
                  <input
                    type="number" min={0}
                    value={newVal}
                    onChange={(e) => setEdit(c.id, Number(e.target.value), c.portions)}
                    className="w-16 bg-transparent border-0 border-b border-paper-300 text-right tabular-nums py-1 focus:outline-none focus:border-ink"
                  />
                  <span className={`text-[10px] tabular-nums w-8 text-right ${delta === 0 ? "text-paper-300" : delta > 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {delta === 0 ? "—" : (delta > 0 ? `+${delta}` : delta)}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Sticky save bar on mobile when there are pending edits */}
      {changedCount > 0 ? (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-20 border-t border-paper-300 bg-paper-50 px-4 py-3 flex items-center gap-3">
          <span className="text-xs text-paper-600">
            {changedCount} edited · <span className={totalDelta < 0 ? "text-red-700" : "text-paper-700"}>
              {totalDelta > 0 ? "+" : ""}{totalDelta}
            </span>
          </span>
          <button className="btn-ghost ml-auto" onClick={reset} disabled={busy}>Discard</button>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function KindChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-xs uppercase tracking-widest border ${active ? "bg-ink text-paper-50 border-ink" : "bg-paper-50 text-paper-600 border-paper-300 hover:border-ink hover:text-ink"}`}
    >
      {label}
    </button>
  );
}
